import { cn } from "@/lib/utils";

export type ChangelogType =
	| "feature"
	| "improvement"
	| "deprecation"
	| "bug-fix";

export const changelogTypeLabels: Record<ChangelogType, string> = {
	feature: "Feature",
	improvement: "Improvement",
	deprecation: "Deprecation",
	"bug-fix": "Bug fix",
};

const typeStyles: Record<
	ChangelogType,
	{
		card: string;
		filterSelected: string;
		badge: string;
		icon: string;
		iconWell: string;
	}
> = {
	feature: {
		card: cn(
			"ring-0",
			"light:bg-card light:border-emerald-400 light:hover:border-emerald-500",
			"dark:border-green-500/40 dark:bg-green-950/25 dark:hover:border-green-500/60",
		),
		filterSelected: cn(
			"border-emerald-500/20 bg-emerald-500/8 text-emerald-700",
			"dark:border-green-500 dark:bg-green-950/30 dark:text-green-400",
		),
		badge: cn(
			"border-transparent",
			"light:bg-emerald-500/10 light:text-emerald-800 light:ring-1 light:ring-emerald-500/20",
			"dark:border-green-500/30 dark:bg-green-950/40 dark:text-green-400",
		),
		icon: cn("light:text-emerald-700", "dark:text-green-400"),
		iconWell: cn(
			"border-transparent",
			"light:bg-emerald-500/10 light:ring-1 light:ring-emerald-500/25",
			"dark:bg-green-950/50",
		),
	},
	improvement: {
		card: cn(
			"ring-0",
			"light:bg-card light:border-sky-400 light:hover:border-sky-500",
			"dark:border-blue-500/40 dark:bg-blue-950/25 dark:hover:border-blue-500/60",
		),
		filterSelected: cn(
			"border-sky-500/20 bg-sky-500/8 text-sky-700",
			"dark:border-blue-500 dark:bg-blue-950/30 dark:text-blue-400",
		),
		badge: cn(
			"border-transparent",
			"light:bg-sky-500/10 light:text-sky-800 light:ring-1 light:ring-sky-500/20",
			"dark:border-blue-500/30 dark:bg-blue-950/40 dark:text-blue-400",
		),
		icon: cn("light:text-sky-700", "dark:text-blue-400"),
		iconWell: cn(
			"border-transparent",
			"light:bg-sky-500/10 light:ring-1 light:ring-sky-500/25",
			"dark:bg-blue-950/50",
		),
	},
	deprecation: {
		card: cn(
			"ring-0",
			"light:bg-card light:border-amber-400 light:hover:border-amber-500",
			"dark:border-yellow-500/40 dark:bg-yellow-950/25 dark:hover:border-yellow-500/60",
		),
		filterSelected: cn(
			"border-amber-500/25 bg-amber-500/10 text-amber-800",
			"dark:border-yellow-400 dark:bg-yellow-950/30 dark:text-yellow-200",
		),
		badge: cn(
			"border-transparent",
			"light:bg-amber-500/12 light:text-amber-900 light:ring-1 light:ring-amber-500/25",
			"dark:border-yellow-500/30 dark:bg-yellow-950/40 dark:text-yellow-200",
		),
		icon: cn("light:text-amber-700", "dark:text-yellow-400"),
		iconWell: cn(
			"border-transparent",
			"light:bg-amber-500/12 light:ring-1 light:ring-amber-500/30",
			"dark:bg-yellow-950/50",
		),
	},
	"bug-fix": {
		card: cn(
			"ring-0",
			"light:bg-card light:border-rose-400 light:hover:border-rose-500",
			"dark:border-red-500/40 dark:bg-red-950/25 dark:hover:border-red-500/60",
		),
		filterSelected: cn(
			"border-rose-500/20 bg-rose-500/8 text-rose-700",
			"dark:border-red-500 dark:bg-red-950/30 dark:text-red-400",
		),
		badge: cn(
			"border-transparent",
			"light:bg-rose-500/10 light:text-rose-800 light:ring-1 light:ring-rose-500/20",
			"dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-400",
		),
		icon: cn("light:text-rose-700", "dark:text-red-400"),
		iconWell: cn(
			"border-transparent",
			"light:bg-rose-500/10 light:ring-1 light:ring-rose-500/25",
			"dark:bg-red-950/50",
		),
	},
};

export function getChangelogTypeCardClass(type: ChangelogType): string {
	return typeStyles[type].card;
}

export function getChangelogTypeFilterSelectedClass(
	type: ChangelogType,
): string {
	return typeStyles[type].filterSelected;
}

export function getChangelogTypeFilterClass(type: ChangelogType): string {
	return getChangelogTypeFilterSelectedClass(type);
}

export function getChangelogTypeBadgeClass(type: ChangelogType): string {
	return cn(
		"inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
		typeStyles[type].badge,
	);
}

export function getChangelogTypeIconClass(type: ChangelogType): string {
	return cn("stroke-[1.75]", typeStyles[type].icon);
}

export function getChangelogTypeIconWellClass(type: ChangelogType): string {
	return cn(
		"flex shrink-0 items-center justify-center rounded-lg",
		typeStyles[type].iconWell,
	);
}

export const changelogFilterIdleClass = cn(
	"border-transparent bg-transparent text-muted-foreground shadow-none",
	"hover:border-border/60 hover:bg-muted/50 hover:text-foreground",
);

export const changelogFilterAllClass = cn(
	changelogFilterIdleClass,
	"data-[selected=true]:border-primary/35 data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary data-[selected=true]:shadow-none",
);

export const changelogVersionSectionClass =
	"border-b border-border/60 pb-12 last:border-b-0 last:pb-0";

export const changelogVersionTitleClass =
	"group mb-5 inline-flex items-center gap-2 rounded-lg text-xl font-semibold tracking-tight text-foreground transition-colors hover:text-primary sm:text-2xl";

export const changelogEmptyStateClass =
	"rounded-xl border border-dashed border-border/80 bg-muted/30 px-6 py-12 text-center text-muted-foreground";
